import { useEffect } from "react";
import { Box, Button, Group, NumberInput, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import {
  applyFiltersAndEnableDailyForOverlimit,
  clearDailyForOverlimitReports,
} from "../../../../redux/features/reports/dailyForOverlimitSlice";
import useDailyForOverlimitFilters, {
  useReportLabels,
} from "./useDailyForOverlimitFilters.logic";
import ExportDailyReports from "../../exportButtons/ExportDailyReports";

const adjustLabel = (label) => (label ? ` (${label}...)` : "");

const DailyForOverlimitFilters = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isLoading, exportPermission, setEnabled } =
    useDailyForOverlimitFilters();
  const { data: labels = {} } = useReportLabels();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      transactionType: "",
      limit: "",
      product: "",
      sumAboveTheLimit: "",
    },
  });

  useEffect(() => {
    return () => {
      onClear();
      setEnabled(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(draftFilters) {
    // Single dispatch → single Redux notification → single React render.
    // Previously we fired 4 dispatches in a row.
    dispatch(applyFiltersAndEnableDailyForOverlimit(draftFilters));
  }

  function onClear() {
    reset();
    dispatch(clearDailyForOverlimitReports());
  }

  return (
    <Box bg="gray.1" p="md">
      <Group justify="space-between">
        <Group>{exportPermission && <ExportDailyReports />}</Group>
        <Group>
          <Button
            disabled={!isDirty}
            variant="outline"
            color="black"
            onClick={onClear}
          >
            Clear
          </Button>
          <Button
            disabled={isLoading || !isDirty}
            onClick={handleSubmit(onSubmit)}
          >
            Generate report
          </Button>
        </Group>
      </Group>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Group mt="md">
          <Controller
            name="transactionType"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                w={270}
                label={
                  t("reports.transactionType") +
                  adjustLabel(labels.transactionType)
                }
              />
            )}
          />
          <Controller
            name="limit"
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <NumberInput
                {...rest}
                value={value === "" ? "" : value}
                onChange={(val) => onChange(val === "" ? "" : val)}
                min={1}
                w={270}
                hideControls
                label={t("reports.limit") + adjustLabel(labels.limit)}
              />
            )}
          />
          <Controller
            name="product"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                w={270}
                label={t("reports.product") + adjustLabel(labels.product)}
              />
            )}
          />
          <Controller
            name="sumAboveTheLimit"
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <NumberInput
                {...rest}
                value={value === "" ? "" : value}
                onChange={(val) => onChange(val === "" ? "" : val)}
                min={1}
                w={270}
                hideControls
                label={
                  t("reports.sumAboveTheLimit") +
                  adjustLabel(labels.sumAboveTheLimit)
                }
              />
            )}
          />
        </Group>
      </form>
    </Box>
  );
};

export default DailyForOverlimitFilters;
